import React, { useState, useEffect, useCallback } from 'react';
import { FiAlertTriangle, FiPlus, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import {
  fetchMonitoringConfig,
  updateMonitoringConfig,
  fetchMonitoringEntities,
  addMonitoringEntity,
  toggleMonitoringEntity,
  deleteMonitoringEntity,
} from '../../utils/sentimentApi';

const userEmail = () => localStorage.getItem('sentiment_user_email') || '';

function validateEntity(type, value) {
  if (type === 'email') {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address.';
  } else {
    if (/@/.test(value)) return 'Domain must not include @. Use acme.com, not @acme.com.';
    if (/^https?:\/\//i.test(value)) return 'Domain must not include http. Use acme.com.';
    if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Enter a valid domain (e.g. acme.com).';
  }
  return null;
}

const EMPTY_FORM = { entity_type: 'email', value: '', label: '' };

export default function SentimentMonitoring({ showToast }) {
  const [mode, setMode]           = useState(null); // 'all' | 'selected'
  const [entityCount, setEntityCount] = useState(0);
  const [entities, setEntities]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const [modeUpdating, setModeUpdating] = useState(false);
  const [toggling, setToggling]   = useState(null); // entity_id
  const [deleting, setDeleting]   = useState(null); // entity_id
  const [deleteConfirm, setDeleteConfirm] = useState(null); // entity object

  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfg, ents] = await Promise.all([fetchMonitoringConfig(), fetchMonitoringEntities()]);
      setMode(cfg.monitor_mode);
      setEntityCount(cfg.entity_count ?? ents.length);
      setEntities(Array.isArray(ents) ? ents : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Mode toggle
  const handleModeToggle = async (newMode) => {
    if (newMode === mode || modeUpdating) return;
    setModeUpdating(true);
    try {
      const res = await updateMonitoringConfig({ monitor_mode: newMode, updated_by: userEmail() });
      setMode(res.monitor_mode);
      showToast?.(`Mode switched to "${res.monitor_mode}"`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setModeUpdating(false);
    }
  };

  // Row toggle (active/inactive)
  const handleToggle = async (entity) => {
    setToggling(entity.entity_id);
    try {
      const updated = await toggleMonitoringEntity(entity.entity_id, !entity.is_active);
      setEntities(prev => prev.map(e => e.entity_id === entity.entity_id ? updated : e));
      showToast?.(`${entity.value} ${updated.is_active ? 'enabled' : 'disabled'}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setToggling(null);
    }
  };

  // Delete
  const handleDelete = async (entity) => {
    setDeleting(entity.entity_id);
    setDeleteConfirm(null);
    try {
      await deleteMonitoringEntity(entity.entity_id);
      setEntities(prev => prev.filter(e => e.entity_id !== entity.entity_id));
      setEntityCount(prev => Math.max(0, prev - 1));
      showToast?.(`${entity.value} removed`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setDeleting(null);
    }
  };

  // Add entity
  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    const valErr = validateEntity(form.entity_type, form.value.trim());
    if (valErr) { setFormError(valErr); return; }
    if (form.label.length > 100) { setFormError('Label must be 100 characters or fewer.'); return; }

    setSubmitting(true);
    try {
      const body = {
        entity_type: form.entity_type,
        value: form.value.trim(),
        ...(form.label.trim() ? { label: form.label.trim() } : {}),
      };
      const created = await addMonitoringEntity(body);
      setEntities(prev => [...prev, created]);
      setEntityCount(prev => prev + 1);
      setForm(EMPTY_FORM);
      setShowForm(false);
      showToast?.(`${created.value} added`, 'success');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

  const activeCount   = entities.filter(e => e.is_active).length;
  const inactiveCount = entities.length - activeCount;

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Email Monitoring</h2>
        <button className="sm-btn sm-btn-ghost" onClick={loadAll} title="Refresh"><FiRefreshCw size={14} /></button>
      </div>

      {error && <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading monitoring config…</span></div>
      ) : (
        <>
          {/* Mode toggle card */}
          <div className="sm-card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Monitoring Mode</div>
                <div style={{ fontSize: 12, color: 'var(--sm-muted)' }}>
                  {mode === 'all'
                    ? 'All inbound interactions are analysed for sentiment.'
                    : `Only ${entityCount} specific emails / domains are monitored.`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--sm-border)' }}>
                <button
                  className={`sm-btn ${mode === 'all' ? 'sm-btn-primary' : 'sm-btn-ghost'}`}
                  style={{ borderRadius: 0, padding: '7px 20px', fontSize: 13 }}
                  onClick={() => handleModeToggle('all')}
                  disabled={modeUpdating}
                >
                  Monitor All
                </button>
                <button
                  className={`sm-btn ${mode === 'selected' ? 'sm-btn-primary' : 'sm-btn-ghost'}`}
                  style={{ borderRadius: 0, padding: '7px 20px', fontSize: 13, borderLeft: '1px solid var(--sm-border)' }}
                  onClick={() => handleModeToggle('selected')}
                  disabled={modeUpdating}
                >
                  Monitor Selected
                </button>
              </div>
            </div>
          </div>

          {/* Entity list header */}
          <div className="sm-section-header" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Monitored Accounts ({entities.length})
              </span>
              {entities.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--sm-muted)' }}>
                  {activeCount} active{inactiveCount > 0 ? ` · ${inactiveCount} paused` : ''}
                </span>
              )}
            </div>
            <button
              className="sm-btn sm-btn-primary"
              onClick={() => { setShowForm(v => !v); setFormError(''); setForm(EMPTY_FORM); }}
              style={{ gap: 6 }}
            >
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add'}
            </button>
          </div>

          {/* Add form */}
          {showForm && (
            <div className="sm-card" style={{ marginBottom: 16 }}>
              <form onSubmit={handleAdd}>
                <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 14px' }}>Add email or domain</h3>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* Type */}
                  <div className="sm-field" style={{ minWidth: 130 }}>
                    <label>Type</label>
                    <select
                      className="sm-select"
                      style={{ width: '100%' }}
                      value={form.entity_type}
                      onChange={e => { setForm(f => ({ ...f, entity_type: e.target.value, value: '' })); setFormError(''); }}
                    >
                      <option value="email">Email</option>
                      <option value="domain">Domain</option>
                    </select>
                  </div>

                  {/* Value */}
                  <div className="sm-field" style={{ flex: '1 1 200px' }}>
                    <label>{form.entity_type === 'email' ? 'Email address *' : 'Domain *'}</label>
                    <input
                      type="text"
                      value={form.value}
                      onChange={e => { setForm(f => ({ ...f, value: e.target.value })); setFormError(''); }}
                      placeholder={form.entity_type === 'email' ? 'john@acme.com' : 'acme.com'}
                      required
                      autoFocus
                    />
                  </div>

                  {/* Label */}
                  <div className="sm-field" style={{ flex: '1 1 200px' }}>
                    <label>Label <span style={{ color: 'var(--sm-muted)', fontWeight: 400 }}>(optional, max 100)</span></label>
                    <input
                      type="text"
                      value={form.label}
                      maxLength={100}
                      onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                      placeholder={form.entity_type === 'email' ? 'John Smith - ACME' : 'ACME Corp (all contacts)'}
                    />
                  </div>
                </div>

                {formError && (
                  <div style={{ fontSize: 12, color: 'var(--sm-red)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FiAlertTriangle size={12} /> {formError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                  <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="sm-btn sm-btn-primary" disabled={submitting}>
                    {submitting ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Entity list */}
          {entities.length === 0 ? (
            <div className="sm-empty">
              <div className="sm-empty-icon">📋</div>
              <span>No accounts in the monitored list. Click <strong>Add</strong> to start.</span>
            </div>
          ) : (
            <div className="sm-card" style={{ padding: 0, overflow: 'hidden' }}>
              {entities.map((entity, i) => (
                <div
                  key={entity.entity_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 18px',
                    borderBottom: i < entities.length - 1 ? '1px solid var(--sm-border)' : 'none',
                    opacity: entity.is_active ? 1 : 0.55,
                    transition: 'opacity .2s',
                  }}
                >
                  {/* Type badge */}
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    padding: '2px 7px',
                    borderRadius: 5,
                    background: entity.entity_type === 'email' ? 'rgba(99,102,241,.12)' : 'rgba(20,184,166,.12)',
                    color: entity.entity_type === 'email' ? 'var(--sm-primary)' : 'var(--sm-teal-1)',
                    flexShrink: 0,
                    minWidth: 52,
                    textAlign: 'center',
                  }}>
                    {entity.entity_type}
                  </span>

                  {/* Value + label */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entity.value}
                    </div>
                    {entity.label && (
                      <div style={{ fontSize: 11, color: 'var(--sm-muted)', marginTop: 1 }}>{entity.label}</div>
                    )}
                  </div>

                  {/* Added by + date */}
                  <div style={{ fontSize: 11, color: 'var(--sm-muted)', textAlign: 'right', flexShrink: 0, display: 'none' }}
                    className="sm-entity-meta">
                    <div>{entity.added_by}</div>
                    <div>{fmtDate(entity.created_at)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--sm-muted)', flexShrink: 0 }}>
                    {fmtDate(entity.created_at)}
                  </div>

                  {/* Active toggle */}
                  <button
                    title={entity.is_active ? 'Disable (keep in list)' : 'Enable'}
                    disabled={toggling === entity.entity_id}
                    onClick={() => handleToggle(entity)}
                    style={{
                      width: 38,
                      height: 22,
                      borderRadius: 11,
                      border: 'none',
                      cursor: 'pointer',
                      background: entity.is_active ? 'var(--sm-green)' : 'var(--sm-border)',
                      position: 'relative',
                      transition: 'background .2s',
                      flexShrink: 0,
                      opacity: toggling === entity.entity_id ? 0.5 : 1,
                    }}
                    aria-label={entity.is_active ? 'Disable' : 'Enable'}
                  >
                    <span style={{
                      position: 'absolute',
                      top: 3,
                      left: entity.is_active ? 19 : 3,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      transition: 'left .2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                    }} />
                  </button>

                  {/* Delete */}
                  <button
                    className="sm-btn sm-btn-ghost"
                    style={{ padding: '4px 6px', color: 'var(--sm-red)', flexShrink: 0 }}
                    title="Remove"
                    disabled={deleting === entity.entity_id}
                    onClick={() => setDeleteConfirm(entity)}
                    aria-label="Remove"
                  >
                    {deleting === entity.entity_id
                      ? <span className="sm-spinner" style={{ width: 12, height: 12 }} />
                      : <FiTrash2 size={14} />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="sm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sm-modal-title">Remove from monitored list?</h3>
            <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>
              <strong>{deleteConfirm.value}</strong>
              {deleteConfirm.label && ` (${deleteConfirm.label})`} will be permanently removed.
            </p>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button
                className="sm-btn sm-btn-danger"
                onClick={() => handleDelete(deleteConfirm)}
              >
                <FiTrash2 size={13} /> Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
